import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from 'lodash';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { IconButton } from '../../components/buttons-visuals';
import { ContentContainer } from '../../components/containers';
import ImageUploader from '../../components/imageUploader';
import { LeagueService } from '../../services/leagueService';

const ComingSoonAssets = (): JSX.Element => {
    const [assets, setAssets] = useState<Core.Models.LeagueComingSoonAsset[]>([]);
    const [name, setName] = useState('');
    const [isDeletingId, setIsDeletingId] = useState<string | undefined>(undefined);

    const load = useCallback(async () => {
        const data = await LeagueService.getComingSoonAssets();
        setAssets(orderBy(data, (asset) => asset.sortOrder));
    }, []);

    useEffect(() => {
        load().catch((e) => toast.error(Core.API.getErrorMessage(e)));
    }, [load]);

    const uploadImage = useCallback(
        async (imageData: string) => {
            const trimmed = name.trim();
            if (!trimmed) {
                toast.error('Enter a name before uploading an image.');
                throw new Error('Name is required.');
            }
            await LeagueService.addComingSoonAsset({ imageData, name: trimmed });
            setName('');
            toast.success('Coming soon title added.');
            await load();
        },
        [load, name]
    );

    const remove = useCallback(
        async (id: string) => {
            setIsDeletingId(id);
            try {
                await LeagueService.deleteComingSoonAsset(id);
                await load();
            } catch (e) {
                toast.error(Core.API.getErrorMessage(e));
            } finally {
                setIsDeletingId(undefined);
            }
        },
        [load]
    );

    return (
        <ContentContainer className="global-container-centered" shade={Core.Models.Shades.Dark40}>
            <p className="mb2x">
                Temporary list for the NCBC registration page. Add a name and image for each title. This can be removed
                later.
            </p>
            <label className="disp-block mb" htmlFor="coming-soon-name">
                Name
            </label>
            <input
                className="form-control mb2x"
                id="coming-soon-name"
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                type="text"
                value={name}
            />
            <ImageUploader
                contentType="image/png"
                disableCrop
                icon="upload"
                text="Upload image"
                uploadImage={uploadImage}
            />
            <ul className="mt2x mb0">
                {assets.map((asset) => (
                    <li className="disp-flex align-center gap mb" key={asset.id}>
                        <img alt="" src={asset.imageUrl} style={{ width: 160, height: 'auto' }} />
                        <span>{asset.name}</span>
                        <IconButton
                            as="button"
                            buttonLabel={`Delete ${asset.name}`}
                            buttonSize="medium"
                            color="destructive"
                            disabled={isDeletingId === asset.id}
                            onClick={() => void remove(asset.id)}
                        >
                            <FontAwesomeIcon icon={['fas', 'trash']} />
                        </IconButton>
                    </li>
                ))}
            </ul>
        </ContentContainer>
    );
};

export default ComingSoonAssets;
